import React, { useState, useEffect } from 'react';
import { Bot, Sparkles, CheckCircle, AlertCircle, Activity } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useAdmin } from '../../hooks/useAdmin';
import { useSubscription } from '../../hooks/useSubscription';

const DAILY_LIMITS = { admin: Infinity, premium: 100, trial: 15 };

type AIProvider = 'openai' | 'anthropic';

export function AISettings() {
  const { user } = useAuth();
  const { isAdmin } = useAdmin();
  const { stripeSubscription } = useSubscription();
  const [provider, setProvider] = useState<AIProvider>('openai');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usageCount, setUsageCount] = useState<number | null>(null);

  const isPremium = stripeSubscription?.subscription_status === 'active' ||
                    stripeSubscription?.subscription_status === 'trialing' ||
                    stripeSubscription?.subscription_status === 'completed';
  const dailyLimit = isAdmin ? DAILY_LIMITS.admin : isPremium ? DAILY_LIMITS.premium : DAILY_LIMITS.trial;
  const usagePercent = dailyLimit === Infinity || usageCount === null ? 0 : Math.round((usageCount / dailyLimit) * 100);
  const remaining = dailyLimit === Infinity ? Infinity : Math.max(0, dailyLimit - (usageCount ?? 0));

  useEffect(() => {
    if (user) {
      loadUserPreference();
      loadUsageCount();
    }
  }, [user]);

  const loadUsageCount = async () => {
    if (!user) return;
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    // These two exclusions must match `checkDailyRateLimit` in
    // supabase/functions/ai-analysis/index.ts, because this number is rendered as
    // "<used> / <dailyLimit>" against that limiter's ceiling. Counting rows the
    // limiter ignores told a pastor they were closer to their cap than they were.
    //
    // Image generation, commentary and social slides have their own buckets. The
    // `v2.` rows belong to SermonBuild 2.0, which shares this database and meters
    // itself.
    const { count } = await supabase
      .from('ai_usage_logs')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .gte('created_at', since)
      .not('request_type', 'in', '("image-generation","commentary","social-slides")')
      .not('request_type', 'like', 'v2.%');
    setUsageCount(count ?? 0);
  };

  const loadUserPreference = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('ai_provider')
        .eq('id', user?.id)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error loading AI preference:', fetchError);
      }
      
      if (data?.ai_provider) {
        setProvider(data.ai_provider as AIProvider);
      }
    } catch (err) {
      console.error('Failed to load AI preference:', err);
    } finally {
      setLoading(false);
    }
  };

  const savePreference = async (newProvider: AIProvider) => {
    if (!user) return;
    
    try {
      setSaving(true);
      setError(null);
      setProvider(newProvider);

      const { error: upsertError } = await supabase
        .from('profiles')
        .update({ ai_provider: newProvider })
        .eq('id', user.id);

      if (upsertError) throw upsertError;

      setSuccess('AI provider preference saved!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error saving AI preference:', err);
      setError(err.message || 'Failed to save preference');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-20 bg-gray-200 rounded mb-4"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
      <div className="flex items-center mb-6">
        <Bot className="h-6 w-6 text-electric mr-3" />
        <h2 className="text-xl font-semibold text-midnight">AI Provider</h2>
      </div>

      <p className="text-slate mb-6">
        Choose which AI model powers SermonBuild's features like text enhancement,
        sermon analysis, translation, and more.
      </p>

      {/* Daily usage meter */}
      <div className={`mb-6 p-4 rounded-lg border ${
        usagePercent >= 100 ? 'bg-crimson/5 border-crimson/20' :
        usagePercent >= 80  ? 'bg-gold/5 border-gold/20' :
        'bg-sand/10 border-sand/20'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Activity className={`h-4 w-4 ${
              usagePercent >= 100 ? 'text-crimson' :
              usagePercent >= 80  ? 'text-gold' : 'text-slate'
            }`} />
            <span className="text-sm font-medium text-midnight">Daily AI Requests</span>
          </div>
          <span className="text-sm font-mono text-slate">
            {usageCount === null ? '…' : usageCount} / {dailyLimit === Infinity ? '∞' : dailyLimit}
          </span>
        </div>
        {dailyLimit !== Infinity && usageCount !== null && (
          <div className="h-2 bg-sand/30 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                usagePercent >= 100 ? 'bg-crimson' :
                usagePercent >= 80  ? 'bg-gold' : 'bg-emerald'
              }`}
              style={{ width: `${Math.min(usagePercent, 100)}%` }}
            />
          </div>
        )}
        <p className="text-xs text-slate">
          {isAdmin
            ? 'Admin account — unlimited requests'
            : usagePercent >= 100
              ? 'Daily limit reached · resets within 24 hours of your first request today'
              : dailyLimit === Infinity
                ? 'Unlimited requests'
                : `${remaining} of ${dailyLimit} remaining · rolling 24-hour window`}
        </p>
      </div>

      {success && (
        <div className="mb-4 p-3 bg-emerald/10 border border-emerald/20 rounded-lg flex items-center text-emerald">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-crimson/10 border border-crimson/20 rounded-lg flex items-center text-crimson">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      <div className="space-y-4">
        <button
          onClick={() => savePreference('openai')}
          disabled={saving}
          className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
            provider === 'openai'
              ? 'border-electric bg-electric/5'
              : 'border-sand/30 hover:border-electric/50'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${
                provider === 'openai' ? 'bg-electric text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-midnight">OpenAI GPT-5.5</h3>
                <p className="text-sm text-slate mt-1">
                  Latest GPT model with excellent reasoning and writing capabilities
                </p>
              </div>
            </div>
            {provider === 'openai' && (
              <CheckCircle className="h-5 w-5 text-electric flex-shrink-0" />
            )}
          </div>
        </button>

        <button
          onClick={() => savePreference('anthropic')}
          disabled={saving}
          className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
            provider === 'anthropic'
              ? 'border-electric bg-electric/5'
              : 'border-sand/30 hover:border-electric/50'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${
                provider === 'anthropic' ? 'bg-electric text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-midnight">Claude Sonnet 4.5</h3>
                <p className="text-sm text-slate mt-1">
                  Anthropic's most capable model, excellent for complex writing and reasoning
                </p>
              </div>
            </div>
            {provider === 'anthropic' && (
              <CheckCircle className="h-5 w-5 text-electric flex-shrink-0" />
            )}
          </div>
        </button>
      </div>

      <div className="mt-6 p-4 bg-sand/10 rounded-lg">
        <p className="text-sm text-slate">
          <strong>Note:</strong> Your preference will apply to all AI-powered features 
          in SermonBuild. Some features like image generation and text-to-speech 
          always use specialized models regardless of this setting.
        </p>
      </div>
    </div>
  );
}
